import React from 'react';
import PropTypes from 'prop-types';
import NavigationPrompt from 'react-router-navigation-prompt';
import { assoc, equals, isEmpty } from 'ramda';
import { DangerButton, PrimaryButton, SecondaryButton } from 'components/Button';
import { Overlay, NoDataOverlay } from 'components/Overlay';
import { Loader } from 'components/Loader';
import { ModalContainer } from 'components/ModalContainer';
import { DeleteItemDialog } from 'components/DeleteItemDialog';
import { ErrorDialog } from 'components/ErrorDialog';
import { NavigateAwayDialog } from 'components/NavigateAwayDialog';
import { TitleInput } from 'components/TitleInput';
import {
	Header,
	Content,
	Fieldset,
	FieldsetTitle,
	Textarea,
	ValidationError,
	Actions,
} from 'admin/Tab';
import { SolutionsSelect } from 'admin/SolutionsSelect';
import {
	getSolutions,
	getFile,
	createFile,
	uploadFile,
	updateFile,
	deleteFile,
} from 'utils/apiUtils';
import { sortByName } from 'utils/sortUtils';
import { mergeNullKeysToDefaults } from 'utils/parseUtils';
import {
	formatBytesInKiloBytes,
	formatFileExtension,
	isValidateFileType,
	getFileNameFromUrl,
} from 'utils/fileUtils';
import { getPageUrl } from 'utils/urlUtils';
import { useIsMounted } from 'hooks/useIsMounted';
import { SaveIcon } from 'components/icons/SaveIcon';

import './File.css';

const DEFAULT_ITEM = {
	name: 'Unnamed File',
	fileName: '',
	description: '',
	solutionId: null,
};

const WHITELISTED_FILE_TYPES = [
	'.csv',
	'.doc',
	'.docx',
	'.png',
	'.jpg',
	'.jpeg',
	'.pdf',
	'.ppt',
	'.pptx',
	'.rtf',
	'.txt',
	'.xls',
	'.xlsx',
	'.zip',
	'.7z',
	'.rar',
	'.gz',
	'.mp4',
	'.mov',
	'.avi',
	'.flv',
	'.wmv',
];

function getValidItem(item) {
	return {
		...item,
		name: item.name.trim(),
		description: item.description.trim(),
	};
}

function getValidationErrors(item, data, isNew) {
	const { name, solutionId, fileName } = item;
	let errors = {};
	if (!name) {
		errors = assoc('name', 'Please enter a valid name', errors);
	}
	if (!solutionId) {
		errors = assoc('solutionId', 'Please select a solution', errors);
	}
	if ((isNew && !data) || (fileName && !isValidateFileType(fileName))) {
		errors = assoc(
			'data',
			`Please try to upload a	file in following format: ${WHITELISTED_FILE_TYPES.join(
				', '
			)}.`,
			errors
		);
	}

	return errors;
}

export function File({ fileId, solutionId, history }) {
	const isNew = !fileId;

	const [isLoading, setIsLoading] = React.useState(true);
	const [isSaving, setIsSaving] = React.useState(false);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [solutions, setSolutions] = React.useState([]);
	const [committedItem, setCommittedItem] = React.useState(null);
	const [liveItem, setLiveItem] = React.useState(null);
	const [committedFile, setCommittedFile] = React.useState({});
	const [liveFile, setLiveFile] = React.useState({});
	const [deletingItem, setDeletingItem] = React.useState(null);
	const [validationErrors, setValidationErrors] = React.useState({});
	const [apiErrors, setApiErrors] = React.useState({});
	const isMounted = useIsMounted();

	const productURL = getPageUrl('product');

	React.useEffect(() => {
		let queries = [getSolutions()];
		if (!isNew) {
			queries = [...queries, getFile(fileId)];
		}
		Promise.all(queries)
			.then(([solutions, item]) => {
				if (!isMounted()) {
					return;
				}

				const formattedItem = mergeNullKeysToDefaults(
					{ ...DEFAULT_ITEM, solutionId },
					item
				);
				setSolutions(sortByName(solutions));
				setCommittedFile(assoc('metadata', formattedItem));
				setLiveFile(assoc('metadata', formattedItem));
				setCommittedItem(formattedItem);
				setLiveItem(formattedItem);
				setIsLoading(false);
			})
			.catch(error => {
				if (!isMounted()) {
					return;
				}
				setIsLoading(false);
				setApiErrors({ file: error });
			});
	}, []);

	function onCancel() {
		if (solutionId) {
			history.push(`${productURL}/${solutionId}`);
			return;
		}
		history.push('/admin/files');
	}

	function onSave() {
		const validItem = getValidItem(liveItem);
		const errors = getValidationErrors(validItem, liveFile.data, isNew);

		if (!isEmpty(errors)) {
			setValidationErrors(errors);
			return;
		}
		setValidationErrors({});
		setIsSaving(true);
		const action = isNew ? createFile : updateFile;

		action(validItem)
			.then(item => {
				if (!isMounted()) {
					return;
				}
				if (!liveFile.data) {
					onFileSaveSuccess(item);
					return;
				} else {
					uploadFile(item.url, liveFile.data)
						.then(() => {
							if (!isMounted()) {
								return;
							}

							onFileSaveSuccess(item);
						})
						.catch(error => {
							if (!isMounted()) {
								return;
							}

							setIsSaving(false);
							setApiErrors({ file: error });
						});
				}
			})
			.catch(error => {
				if (!isMounted()) {
					return;
				}
				setIsSaving(false);
				setApiErrors({ file: error });
			});
	}

	function onDelete() {
		setIsDeleting(true);
		deleteFile(deletingItem)
			.then(() => {
				if (!isMounted()) {
					return;
				}
				setIsDeleting(false);
				if (solutionId) {
					history.push(`${productURL}/${solutionId}`);
					return;
				}
				history.push('/admin/files');
			})
			.catch(error => {
				if (!isMounted()) {
					return;
				}
				setIsDeleting(false);
				setApiErrors({ file: error });
			});
	}

	function onFileSaveSuccess(item) {
		setCommittedFile(assoc('metadata', {}));
		setLiveFile(assoc('metadata', {}));
		setCommittedItem(item);
		setLiveItem(item);
		setIsSaving(false);
		if (solutionId) {
			history.push(`${productURL}/${solutionId}`);
			return;
		}
		if (isNew) {
			history.push(`/admin/files/${item.id}`);
			refetchFile(item.id);
		} else {
			refetchFile(item.id);
		}
	}

	function refetchFile(id) {
		setIsLoading(true);
		getFile(id)
			.then(item => {
				if (!isMounted()) {
					return;
				}
				setCommittedItem(item);
				setLiveItem(item);
				setCommittedFile(assoc('metadata', item));
				setLiveFile(assoc('metadata', item));
				setIsLoading(false);
			})
			.catch(error => {
				if (!isMounted()) {
					return;
				}
				setIsLoading(false);
				setApiErrors({ file: error });
			});
	}

	function closeDeleteItemDialog() {
		setDeletingItem(null);
	}

	function closeErrorDialog() {
		setApiErrors({});
	}

	if (isLoading) {
		return (
			<Overlay>
				<Loader />
			</Overlay>
		);
	}

	if (!liveItem) {
		return <NoDataOverlay />;
	}

	const fileSize = formatBytesInKiloBytes(liveItem.sizeInBytes);
	const fileExtension = formatFileExtension(liveItem.extension);

	return (
		<form>
			<Header>
				<TitleInput
					theme="admin"
					value={liveItem.name}
					onChange={event => setLiveItem(assoc('name', event.target.value))}
					autoSelect={isNew}
					autoFocus={!isNew}
				/>
				{validationErrors.name && (
					<ValidationError>{validationErrors.name}</ValidationError>
				)}
				<Actions>
					{!isNew && (
						<DangerButton disabled={isDeleting} onClick={() => setDeletingItem(liveItem)}>
							Delete
						</DangerButton>
					)}
					<SecondaryButton style={{ marginLeft: 'auto' }} onClick={onCancel}>
						Cancel
					</SecondaryButton>
					<PrimaryButton
						disabled={
							(equals(liveItem, committedItem) && equals(liveFile, committedFile)) ||
							isSaving
						}
						onClick={onSave}
					>
						{isNew && (isSaving ? 'Creating…' : 'Create')}
						{!isNew && (isSaving ? 'Updating…' : 'Update')}
					</PrimaryButton>
				</Actions>
			</Header>

			<Content>
				<Fieldset>
					<FieldsetTitle>
						<label htmlFor="solutions-select">Solution</label>
					</FieldsetTitle>
					<SolutionsSelect
						theme="admin"
						solutions={solutions}
						groups={[]}
						value={liveItem.solutionId}
						onChange={solutionId => setLiveItem(assoc('solutionId', solutionId))}
					/>
					{validationErrors.solutionId && (
						<ValidationError>{validationErrors.solutionId}</ValidationError>
					)}
				</Fieldset>
				<Fieldset>
					<FieldsetTitle>
						<label htmlFor="file-description">Description</label>
					</FieldsetTitle>
					<Textarea
						id="file-description"
						value={liveItem.description}
						onChange={event => setLiveItem(assoc('description', event.target.value))}
					/>
				</Fieldset>
				{liveItem.fileName && !isEmpty(liveFile.metadata) && (
					<Fieldset>
						<FieldsetTitle>Current file</FieldsetTitle>
						<a
							className="file__link"
							href={liveFile.metadata.url}
							target="_blank"
							rel="noopener noreferrer"
						>
							{getFileNameFromUrl(liveFile.metadata.url)}{' '}
							{`(${fileSize}, ${fileExtension})`}
							<SaveIcon size={20} />
						</a>
					</Fieldset>
				)}
				<Fieldset>
					<FieldsetTitle>
						<label htmlFor="file-picker">Upload new file</label>
					</FieldsetTitle>
					<input
						type="file"
						id="file-picker"
						name="file-picker"
						accept={WHITELISTED_FILE_TYPES.join(',')}
						onChange={event => {
							const file = event.target.files[0];
							setLiveItem(assoc('fileName', file.name));
							setLiveFile(assoc('data', file));
						}}
					/>
					{validationErrors.data && (
						<ValidationError>{validationErrors.data}</ValidationError>
					)}
				</Fieldset>
			</Content>

			{deletingItem && (
				<ModalContainer onClose={closeDeleteItemDialog}>
					<DeleteItemDialog
						type="file"
						name={deletingItem.name}
						isDeleting={isDeleting}
						onCancel={closeDeleteItemDialog}
						onConfirm={onDelete}
					/>
				</ModalContainer>
			)}

			{!isEmpty(apiErrors) && (
				<ModalContainer onClose={closeErrorDialog}>
					<ErrorDialog
						type="API"
						errors={apiErrors}
						button={
							<SecondaryButton onClick={() => window.location.reload()}>
								Reload
							</SecondaryButton>
						}
						onClose={closeErrorDialog}
					/>
				</ModalContainer>
			)}

			<NavigationPrompt
				disableNative
				when={(currentLocation, nextLocation) =>
					currentLocation.pathname !== nextLocation.pathname &&
					!equals(liveItem, committedItem)
				}
			>
				{({ onConfirm, onCancel }) => (
					<ModalContainer onClose={onCancel}>
						<NavigateAwayDialog onCancel={onCancel} onConfirm={onConfirm} />
					</ModalContainer>
				)}
			</NavigationPrompt>
		</form>
	);
}

File.propTypes = {
	fileId: PropTypes.number,
	solutionId: PropTypes.number,
	history: PropTypes.shape({
		push: PropTypes.func.isRequired,
	}).isRequired,
};
